import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";

const ACTION_IMG =
  "https://images.pexels.com/photos/32024627/pexels-photo-32024627.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1000";

const ROLES = ["Player", "Manager", "Sponsor", "Umpire", "Volunteer"];
const EXPERIENCE = ["High School", "College", "Semi-Pro / Pro", "Recreational", "Just a Fan"];

const inputCls =
  "w-full bg-[#141414] border border-white/20 px-4 py-3 text-white placeholder:text-[#5b626b] focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors font-medium";
const labelCls = "text-xs font-bold uppercase tracking-[0.2em] text-[#8A94A0] mb-2 block";

export const SignupForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Player",
    experience: "",
    position: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/signups", form);
      setDone(true);
      toast.success("You're on the list! We'll be in touch. ⚾");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup" data-testid="signup-section" className="relative py-24 lg:py-32 bg-[#0A0A0A] border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div className="lg:sticky lg:top-28 self-start">
          <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-[0.3em]">Join WABL</span>
          <h2 className="text-4xl sm:text-5xl uppercase tracking-tighter mt-4 leading-[0.95]">
            Claim Your Spot On The Roster
          </h2>
          <p className="mt-6 text-[#C7CDD4] text-lg font-medium leading-relaxed">
            Fill out the form and we'll reach out with next steps. Invite your baseball friends — the more, the better.
          </p>
          <div className="mt-10 aspect-video overflow-hidden border border-white/10 hidden lg:block">
            <img src={ACTION_IMG} alt="Baseball action" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className="bg-[#141414] border border-white/10 p-7 sm:p-10">
          {done ? (
            <motion.div
              data-testid="signup-success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center text-center py-16"
            >
              <CheckCircle2 className="text-[#FF3B30] mb-6" size={64} strokeWidth={1.5} />
              <h3 className="text-3xl uppercase tracking-tight">You're In!</h3>
              <p className="mt-4 text-[#8A94A0] font-medium max-w-sm">
                Thanks for signing up to the Weekend Adult Baseball League. We'll be in touch soon. See you on the
                diamond. ⚾
              </p>
              <button
                data-testid="signup-another-btn"
                onClick={() => {
                  setDone(false);
                  setForm({ name: "", email: "", phone: "", role: "Player", experience: "", position: "", message: "" });
                }}
                className="mt-8 font-heading uppercase tracking-wide border border-white/20 hover:border-white px-6 py-3 transition-colors"
              >
                Submit Another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="signup-form" className="space-y-6">
              <div>
                <label className={labelCls}>I want to join as a</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ROLES.map((r) => (
                    <button
                      type="button"
                      key={r}
                      data-testid={`role-select-${r.toLowerCase()}`}
                      onClick={() => set("role", r)}
                      className={`py-3 text-sm font-bold uppercase tracking-wide border transition-all ${
                        form.role === r
                          ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                          : "bg-transparent border-white/20 text-[#8A94A0] hover:border-white/50"
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="su-name">Full Name *</label>
                  <input
                    id="su-name"
                    data-testid="signup-name"
                    className={inputCls}
                    placeholder="John Doe"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="su-email">Email *</label>
                  <input
                    id="su-email"
                    type="email"
                    data-testid="signup-email"
                    className={inputCls}
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="su-phone">Phone</label>
                  <input
                    id="su-phone"
                    data-testid="signup-phone"
                    className={inputCls}
                    placeholder="(555) 123-4567"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                  />
                </div>
                <div>
                  <label className={labelCls} htmlFor="su-position">Position (optional)</label>
                  <input
                    id="su-position"
                    data-testid="signup-position"
                    className={inputCls}
                    placeholder="e.g. Pitcher, SS"
                    value={form.position}
                    onChange={(e) => set("position", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Experience Level</label>
                <div className="flex flex-wrap gap-2">
                  {EXPERIENCE.map((ex) => (
                    <button
                      type="button"
                      key={ex}
                      data-testid={`experience-${ex.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      onClick={() => set("experience", ex)}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border transition-all ${
                        form.experience === ex
                          ? "bg-white text-[#0A0A0A] border-white"
                          : "border-white/20 text-[#8A94A0] hover:border-white/50"
                      }`}
                    >
                      {ex}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="su-message">Anything else?</label>
                <textarea
                  id="su-message"
                  data-testid="signup-message"
                  rows={4}
                  className={inputCls}
                  placeholder="Tell us about your baseball background, questions, or how you'd like to help..."
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                />
              </div>

              <button
                type="submit"
                data-testid="signup-submit-button"
                disabled={loading}
                className="w-full font-heading uppercase tracking-wide bg-[#FF3B30] text-white hover:bg-[#FF6B63] disabled:opacity-60 transition-colors px-8 py-4 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="animate-spin" size={20} /> Submitting...</> : "Sign Me Up"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
